import { baseApi } from "./baseApi";
import { PaginationArgs, ScopeType } from "types/globalTypes";
import { buildQueryParams } from "services/helper";
import {
  LicenseDTO,
  LicensePlanListResponse,
  LicensesListResponse,
  PlanDto,
  PurchaseLicenseDto,
  PurchaseLicenseResponse,
} from "types/assessmentTypes";

export const licenseApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // license
    getLicenses: builder.query<
      LicensesListResponse,
      PaginationArgs & {
        scope?: ScopeType;
      }
    >({
      query: ({ scope, ...args }) => {
        const scopePaths: Record<ScopeType, string> = {
          admin: `/admin/license`,
          organization: `/user/license-organization`,
          user: `/user/license-company`,
          responder: ``,
        };
        const path = scopePaths[scope];
        return {
          url: `${path}?${buildQueryParams(args || {})}`,
        };
      },
      providesTags: ["Licenses"],
    }),

    createLicense: builder.mutation<void, PurchaseLicenseDto>({
      query: (data) => ({
        url: "/admin/license/grant",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Licenses"],
    }),

    getPlans: builder.query<PlanDto[], PaginationArgs>({
      query: (args) => ({
        url: `/license-plans?${buildQueryParams(args || {})}`,
      }),
      providesTags: ["Plans"],
    }),

    getCallback: builder.query<
      { redirectUrl: string; success: boolean; transactionId: number },
      { transId: number; id: number }
    >({
      query: ({ transId, id }) => ({
        url: `/payment/callback?trans_id=${transId}&id_get=${id}`,
      }),
    }),

    purchaseLicense: builder.mutation<
      PurchaseLicenseResponse,
      PurchaseLicenseDto & { callback: string }
    >({
      query: (data) => ({
        url: "/payment/purchase",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Licenses"],
    }),

    upgradePlanLicense: builder.mutation<
      {
        amount: number;
        authority: string;
        redirectUrl: string;
        transactionId: number;
      },
      { assessmentId: number; callback: string }
    >({
      query: ({ assessmentId, callback }) => ({
        url: `/payment/license/upgrade/${assessmentId}`,
        method: "POST",
        body: { callback },
      }),
    }),

    availableOrganizationLicense: builder.query<
      LicenseDTO | false,
      { organizationId: number; companyId: number }
    >({
      query: (data) => ({
        url: `/user/license-company/organization/available`,
        method: "POST",
        body: data,
      }),
    }),

    // admin organization plan
    getOrganizationLicensePlans: builder.query<
      LicensePlanListResponse,
      PaginationArgs
    >({
      query: (args) => ({
        url: `/admin/organization-license-plan?${buildQueryParams(args || {})}`,
      }),
      providesTags: ["OrganizationLicensePlans"],
    }),

    createOrganizationPlan: builder.mutation<void, Partial<PlanDto>>({
      query: (data) => ({
        url: `/admin/organization-license-plan`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["OrganizationLicensePlans"],
    }),

    updateOrganizationPlan: builder.mutation<
      void,
      { id: number; data: Partial<PlanDto> }
    >({
      query: ({ id, data }) => ({
        url: `/admin/organization-license-plan/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["OrganizationLicensePlans"],
    }),

    deleteOrganizationPlan: builder.mutation<void, number>({
      query: (id) => ({
        url: `/admin/organization-license-plan/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["OrganizationLicensePlans"],
    }),

    // admin license plan
    getLicensePlans: builder.query<LicensePlanListResponse, PaginationArgs>({
      query: (args) => ({
        url: `/admin/license-plan?${buildQueryParams(args || {})}`,
      }),
      providesTags: ["LicensePlans"],
    }),

    createPlan: builder.mutation<void, Partial<PlanDto>>({
      query: (data) => ({
        url: `/admin/license-plan`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["LicensePlans"],
    }),

    updatePlan: builder.mutation<void, { id: number; data: Partial<PlanDto> }>({
      query: ({ id, data }) => ({
        url: `/admin/license-plan/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["LicensePlans"],
    }),

    deletePlan: builder.mutation<void, number>({
      query: (id) => ({
        url: `/admin/license-plan/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["LicensePlans"],
    }),
  }),
});

export const {
  // license
  useGetLicensesQuery,
  useCreateLicenseMutation,
  useGetPlansQuery,
  useGetCallbackQuery,
  usePurchaseLicenseMutation,
  useUpgradePlanLicenseMutation,
  useAvailableOrganizationLicenseQuery,
  useLazyAvailableOrganizationLicenseQuery,
  // admin organization plan
  useGetOrganizationLicensePlansQuery,
  useCreateOrganizationPlanMutation,
  useUpdateOrganizationPlanMutation,
  useDeleteOrganizationPlanMutation,
  // admin license plan
  useGetLicensePlansQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useDeletePlanMutation,
} = licenseApi;
